import { Typography } from "@mui/joy";
import { memo, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import {
  AutomaticThoughtForValueKeyType,
  AutomaticThoughtForValueType,
  AutomaticThoughtKeyType,
  AutomaticThoughtType,
  ConceptKeyType,
  CopingStrategyKeyType,
  CopingStrategyType,
  CoreBeliefKeyType,
  CoreBeliefType,
  IntermediateBeliefKeyType,
  IntermediateBeliefType,
} from "api/conceptApi";
import { CellType, levelType } from "data/CellComponent";
import { ProgramContentType } from "data/BlockComponent";
import * as _ from "lodash";
import { thoughtTrapMapping, thoughtTrapMappingKey } from "data/task/v1/4-5-A";
import { useTranslation } from "react-i18next";
import { conceptAtom } from "recoil/conceptAtom";
import {
  getFirstFilledAutomaticThoughtKey,
  getTaskIdFromTaskKey,
} from "logic/logics";
import { theme } from "styles/theme";

function TypographyFromDashboardCell({
  taskKey,
  initialValue,
  setData,
  lineIndex,
  cellIndex,
  blockIndex,
  id1,
  id2,
  level,
}: {
  taskKey: string;
  initialValue?: string;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  blockIndex: number;
  id1: ConceptKeyType;
  id2?:
    | AutomaticThoughtKeyType
    | AutomaticThoughtForValueKeyType
    | CopingStrategyKeyType
    | IntermediateBeliefKeyType
    | CoreBeliefKeyType;
  level?: levelType;
}) {
  const { t: t_thinkingTrap } = useTranslation("translation", {
    keyPrefix: "thinkingTrap",
  });

  const [value, setValue] = useState<string>();

  const conceptState = useRecoilValue(conceptAtom);
  // const conceptState = useRecoilValue(conceptNoteAtom);

  useEffect(() => {
    if (conceptState) {
      // 9-2-A 는 특정 자동적 사고에 종속되지 않는 활동지라,
      // 참조하는 사고(1번)가 결번이면 남아 있는 첫 번째 사고를 보여준다.
      const conceptKey =
        getTaskIdFromTaskKey(taskKey) === "9-2-A" && !conceptState[id1]
          ? getFirstFilledAutomaticThoughtKey(conceptState) || id1
          : id1;
      const value = id2
        ? (
            (conceptState[conceptKey] || {}) as AutomaticThoughtType &
              AutomaticThoughtForValueType &
              CopingStrategyType &
              IntermediateBeliefType &
              CoreBeliefType
          )[id2]
        : ((conceptState[conceptKey] || "") as string);
      // console.log(conceptState, id2, value);

      setValue(value);

      setData((data) => {
        if (data) {
          const data_temp = _.cloneDeep(data);

          const isAddThoughtTrapCell =
            getTaskIdFromTaskKey(taskKey).startsWith("6-12-A") &&
            id1.startsWith("automaticThought") &&
            id2 === "behavior";
          if (isAddThoughtTrapCell) {
            const automaticThoughtId = id1.substring("automaticThought".length);

            const cells = data_temp[blockIndex].lines.flat();
            const thinkingTrapLine: CellType[] = [
              {
                type: "temp",
                content: {
                  id: `automaticThought${automaticThoughtId}_thoughtTrap`,
                  value: (
                    conceptState[id1] as AutomaticThoughtType
                  )?.thoughtTrapList
                    ?.map((each) =>
                      t_thinkingTrap(
                        thoughtTrapMapping[
                          each.split("_")[0] as thoughtTrapMappingKey
                        ]
                          .split("-")[0]
                          .trim()
                      )
                    )
                    .join(", "),
                },
              },
              // {
              //   type: "temp",
              //   content: {
              //     id: `대처전략`,
              //     value: (conceptState[id1] as AutomaticThoughtType)
              //       ?.copingStrategy,
              //   },
              // },
            ];

            if (
              !cells.find(
                (element) =>
                  element.type === "temp" &&
                  element.content.id ===
                    `자동적사고${automaticThoughtId} 생각함정`
              )
            ) {
              data_temp[blockIndex].lines.push(thinkingTrapLine);
            }
          }
          const currentContent =
            data_temp[blockIndex].lines[lineIndex][cellIndex].content || {};
          if (isAddThoughtTrapCell || currentContent.value !== value) {
            data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
              ...currentContent,
              htmlString: `<p>${value}</p>`,
              value: value,
            };

            return data_temp;
          }
        }
        return data;
      });
    }
  }, [conceptState]);

  return (
    <Typography
      variant={"Body"}
      sx={{ color: theme.vars.palette.Content.Strong, fontWeight: 500 }}
    >
      {value}
    </Typography>
  );
}

export default memo(TypographyFromDashboardCell);
